/**
 * Get a number parameter from the URL.
 * @param key The parameter key.
 * @param fallback The fallback value if the parameter is not found or invalid.
 * @returns The parameter value or the fallback.
 */
export function getNumberParam(key: string, fallback: number): number {

  // If no window, return fallback
  if (typeof window === 'undefined' || !window.location) {
    return fallback;
  }

  // Parse the URL parameters
  try {
    const params = new URLSearchParams(window.location.search || '');
    const raw = params.get(key);
    if (raw === null) return fallback;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  catch {
    // If something fails, return fallback
    return fallback;
  }

}

/**
 * Get a string parameter from the URL.
 * @param key The parameter key.
 * @param fallback The fallback value if the parameter is not found or invalid.
 * @returns The parameter value or the fallback.
 */
export function getStringParam(key: string, fallback: string): string {

  // If no window, return fallback
  if (typeof window === 'undefined' || !window.location) {
    return fallback;
  }

  // Parse the URL parameters
  try {
    const params = new URLSearchParams(window.location.search || '');
    const raw = params.get(key);
    return raw === null ? fallback : raw;
  }
  catch {
    // If something fails, return fallback
    return fallback;
  }

}

/**
 * Check if a boolean parameter exists in the URL.
 * @param key The parameter key.
 * @returns True if the parameter exists (regardless of value), false otherwise.
 */
export function getBooleanParam(key: string): boolean {

  // If no window, return false
  if (typeof window === 'undefined' || !window.location) {
    return false;
  }

  // Parse the URL parameters
  try {
    const params = new URLSearchParams(window.location.search || '');
    return params.has(key);
  }
  catch {
    // If something fails, return false
    return false;
  }

}
